import cs from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Button,
    Col,
    Row,
    Space,
    Table,
    Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { IDriver } from '../../../../interfaces/driver.interface';
import { checkDriverStatus } from '../../../../utils/driver.util';
import { IPagination } from '../../../../interfaces/paginate.interface';
import { useWorkspaceStore } from '../../../../store/workspace.store';
import { getDrivers } from '../../../../api/drivers.api';
import { IRequest } from '../../../../interfaces/requests.interface';

interface Props {
    request: IRequest | null;
    handleAssignDriver: (driverID: string, bookingID: string) => void;
}

const { Text } = Typography;

const AvailableDrivers: FC<Props> = ({ request, handleAssignDriver }) => {
    const { t } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const navigate = useNavigate();
    const workspace = useWorkspaceStore((state) => state.data);

    const PAGE = 1;
    const LIMIT = 10;

    const [availableDrivers, setAvailableDrivers] = useState<IDriver[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [pagination, setPagination] = useState<IPagination>({
        limit: LIMIT,
        page: PAGE,
        total: 0,
        totalPages: 0,
    });

    const columns = [
        {
            title: t('FULL_NAME'),
            dataIndex: 'name',
            key: 'name',
            align: 'center' as 'center',
            render: (name: string, driver: IDriver) => (
                <Space direction="vertical" size={4}>
                    <Text>{`${name} ${driver.lastName}`}</Text>
                    <Text type="secondary">{driver.email}</Text>
                </Space>
            ),
        },
        {
            title: t('STATUS'),
            dataIndex: 'status',
            key: 'status',
            align: 'center' as 'center',
            render: (_: string, driver: any) => {
                const { isBusy, onDutyFlag } = driver;
                const status = checkDriverStatus(isBusy, onDutyFlag);
                return (
                    <div className={cs('table-status', status)}>
                        {t(status)}
                    </div>
                );
            },
        },
        {
            title: t('DISTANCE'),
            dataIndex: 'distance',
            key: 'distance',
            align: 'center' as 'center',
            render: (distance: number) => (
                <Text>
                    {`${distance.toFixed(2)} km`}
                </Text>
            ),
        },
        {
            title: t('ACTIONS'),
            dataIndex: 'actions',
            key: 'actions',
            align: 'center' as 'center',
            render: (actions: string, driver: IDriver) => (
                <Button
                    type="primary"
                    onClick={() => {
                        handleAssignDriver(driver._id, request?.bookingID as string);
                        navigate('/bookings/actives');
                    }}
                    size="small"
                >
                    {t('ASSIGN')}
                </Button>
            ),
        },
    ];

    const fetchAvailableDrivers = () => {
        setLoading(true);

        const query = {
            page: pagination.page,
            limit: pagination.limit,
            driverIds: workspace?.drivers || [],
            bookingID: request?.bookingID,
        };

        getDrivers(query)
            .then(({ data: response }) => {
                setTotal(response.total);
                setAvailableDrivers(response.data);
            })
            .catch((error: Error) => {
                const description = error.message || 'An error occurred while fetching available drivers.';
                setErrorMessage(description);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (request?._id) {
            fetchAvailableDrivers();
        }
    }, [request?._id]);

    return (
        <Row gutter={[16, 0]}>
            <Col span={24}>
                {errorMessage && (
                    <Alert message={errorMessage} type="error" />
                )}
                <Table
                    columns={columns}
                    dataSource={availableDrivers}
                    pagination={{
                        total,
                        size: 'small',
                        pageSize: pagination.limit,
                        current: pagination.page,
                        onChange: (page, pageSize) => {
                            setPagination({
                                limit: pageSize,
                                page,
                                total,
                                totalPages: Math.ceil(total / pageSize),
                            });
                        },
                    }}
                    rowKey="_id"
                    loading={loading}
                    className="table"
                    locale={{
                        emptyText: t('NO_DATA'),
                    }}
                />
            </Col>
        </Row>
    );
};

export { AvailableDrivers };
